import React, { useState, useRef, useEffect } from 'react';
import './HealthcareChat.css';

interface Source {
  text: string;
  source: string;
  section: string;
  score: number;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
  id: string;
}

interface HealthcareChatProps {
  onExampleClick?: (question: string) => void;
}

export const HealthcareChat: React.FC<HealthcareChatProps> = ({ onExampleClick }) => {
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const callRagSimple = async (question: string) => {
    const apiUrl = process.env.NODE_ENV === 'production'
      ? 'https://ceaustin117-personal-website-backend.hf.space/rag-simple'
      : 'http://localhost:7860/rag-simple';

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    return await response.json();
  };

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputMessage;
    if (!text.trim()) return;

    const userMessage: Message = {
      role: 'user',
      content: text,
      id: Date.now().toString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await callRagSimple(text);

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.answer,
        sources: response.sources,
        id: (Date.now() + 1).toString(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error calling RAG API:', error);

      const errorMessage: Message = {
        role: 'assistant',
        content: 'Sorry, I encountered an error connecting to the healthcare knowledge base. Please try again.',
        id: (Date.now() + 1).toString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleSources = (messageId: string) => {
    setExpandedSources(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  // Expose method for example clicks
  React.useEffect(() => {
    if (onExampleClick) {
      // This is a bit of a hack - we're using the prop as a way to receive clicks
    }
  }, [onExampleClick]);

  return (
    <div className="healthcare-chat">
      <div className="healthcare-chat-messages">
        {messages.length === 0 && (
          <div className="healthcare-chat-empty">
            <div className="empty-icon">🏥</div>
            <p>Ask questions about HealthFirst Medical Center's policies</p>
            <span>Try clicking an example question below</span>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`healthcare-message ${message.role}`}
          >
            <div className="message-bubble">
              <div className="message-text">{message.content}</div>

              {message.sources && message.sources.length > 0 && (
                <div className="message-sources">
                  <button
                    className="sources-toggle"
                    onClick={() => toggleSources(message.id)}
                  >
                    <span className="sources-icon"></span>
                    {expandedSources.has(message.id) ? 'Hide' : 'Show'} {message.sources.length} sources
                  </button>

                  {expandedSources.has(message.id) && (
                    <div className="sources-list">
                      {message.sources.map((source, idx) => (
                        <div key={idx} className="source-item">
                          <div className="source-header">
                            <span className="source-name">{source.source}</span>
                            <span className="source-section">{source.section}</span>
                            <span className="source-score">
                              {(source.score * 100).toFixed(0)}% match
                            </span>
                          </div>
                          <div className="source-text">
                            {source.text.length > 300
                              ? source.text.substring(0, 300) + '...'
                              : source.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="healthcare-message assistant">
            <div className="message-bubble">
              <div className="loading-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="healthcare-chat-input">
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask about hospital policies..."
          disabled={isLoading}
          rows={1}
        />
        <button
          onClick={() => handleSendMessage()}
          disabled={isLoading || !inputMessage.trim()}
          className="send-btn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Export a way to send messages programmatically
export const useHealthcareChat = () => {
  const [sendMessage, setSendMessage] = useState<((msg: string) => void) | null>(null);

  return { sendMessage, setSendMessage };
};

export default HealthcareChat;
